import { ShiftTypesEnum } from '../../../js/api';
import { ShiftRateTypesEnum } from '../../../js/api/models/ShiftRateTypesEnum';

// Helper function to check if dark mode should be enabled
// globally set to false until we turn dark mode back on
const shouldEnableDarkMode = () => {
  // const isProduction = window.APP_ENV === 'prod' || window.APP_ENV === 'production';
  // if (isProduction) {
  //   return false;
  // }
  // return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  return false;
};

/**
 * Creates react-select styles configuration
 * @param small - If true, applies smaller sizing (matches Input/Button small prop)
 */
// eslint-disable-next-line sonarjs/cognitive-complexity
export const getReactSelectStyles = (small = false) => {
  const controlHeight = small ? '1.5rem' : '2.25rem'; // 24px vs 36px
  const controlMinHeight = small ? '1.5rem' : '2.25rem';
  const valueContainerPadding = small ? '0 0.375rem' : '0 0.875rem'; // 6px vs 14px
  const fontSize = small ? '0.75rem' : '1rem'; // 12px vs 16px
  const optionPadding = small ? '0.25rem 0.625rem' : '0.5rem 1rem'; // 4px 10px vs 8px 16px

  return {
    control: (provided, state) => ({
      ...provided,
      ...(state.isMulti
        ? {
            // For multi-select, do not set height/minHeight so it can expand
          }
        : {
            height: controlHeight,
            minHeight: controlMinHeight,
          }),
      // tw equivalent of zinc-950/10, default for all our other catalyst components
      borderColor: state.isDisabled
        ? 'rgba(9, 9, 11, 0.2)' // zinc-950/20 when disabled
        : 'rgba(9, 9, 11, 0.1)',
      borderRadius: small ? '0.375rem' : '8px',
      padding: '0',
      backgroundColor: state.isDisabled
        ? 'rgba(255, 255, 255, 0.025)' // white/[2.5%] when disabled
        : '#ffffff',
      boxShadow: state.isFocused
        ? '0 0 0 2px #0493a1'
        : state.isDisabled
          ? 'none'
          : '0 1px 2px rgba(0, 0, 0, 0.125)',
      ':hover': {
        ...(provided[':hover'] || {}),
        // Keep ring and border consistent when hovered
        boxShadow: state.isFocused ? '0 0 0 2px #0493a1' : (provided[':hover'] as any)?.boxShadow, // fall back to default for unfocused hover
        borderColor: state.isFocused ? '#0493a1' : 'rgba(9, 9, 11, 0.2)',
      },
      transition: 'box-shadow 0.2s ease-in-out',
      opacity: state.isDisabled ? 0.5 : 1, // Match Input's disabled opacity
      fontSize,
      ...(shouldEnableDarkMode() && {
        backgroundColor: state.isDisabled
          ? 'rgba(255, 255, 255, 0.025)' // white/[2.5%] when disabled
          : 'rgba(255, 255, 255, 0.05)',
        borderColor: state.isDisabled
          ? 'rgba(255, 255, 255, 0.15)' // white/15 when disabled
          : 'rgba(255, 255, 255, 0.1)',
      }),
    }),
    valueContainer: (provided, state) => ({
      ...provided,
      ...(state.isMulti
        ? {
            // For multi-select, do not set height so it can expand
            padding: valueContainerPadding,
            display: 'flex',
            alignItems: 'center',
          }
        : {
            height: controlHeight,
            padding: valueContainerPadding,
            display: 'flex',
            alignItems: 'center',
          }),
    }),
    menu: (provided) => ({
      ...provided,
      borderRadius: '0.375rem',
      boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)',
      backgroundColor: '#f2f2f2',
      ...(shouldEnableDarkMode() && {
        backgroundColor: 'rgb(82, 82, 91)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
      }),
    }),
    option: (provided, state) => ({
      ...provided,
      padding: optionPadding,
      fontSize,
      backgroundColor: state.isSelected ? '#0493a1' : 'transparent',
      color: state.isSelected ? '#ffffff' : '#171717',
      ...(shouldEnableDarkMode() && {
        color: 'rgba(255, 255, 255, 0.9)',
      }),
      cursor: 'pointer',
      ':active': {
        backgroundColor: '#0493a1',
        color: '#ffffff',
      },
    }),
    input: (provided) => ({
      ...provided,
      color: '#171717',
      fontSize,
      margin: 0,
      padding: 0,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: '#171717',
      fontSize,
      ...(shouldEnableDarkMode() && {
        color: 'rgba(255, 255, 255, 0.9)',
      }),
    }),
    multiValue: (provided) => ({
      ...provided,
      ...(shouldEnableDarkMode() && {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
      }),
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize,
      ...(shouldEnableDarkMode() && {
        color: 'rgba(255, 255, 255, 0.9)',
      }),
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      // height: controlHeight,
    }),
  };
};

/**
 * Generates initials from a name string
 * @param name - Full name string (e.g., "John Doe" or "Jane Smith")
 * @returns Uppercase initials (max 2 characters), or "?" if name is empty/null/undefined
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

// Default export for backward compatibility
export const reactSelectReset = getReactSelectStyles(false);

/**
 * Shift type labels for display
 */
export const SHIFT_TYPE_LABELS: Record<ShiftRateTypesEnum, string> = {
  [ShiftRateTypesEnum.DAY]: 'Day',
  [ShiftRateTypesEnum.NIGHT]: 'Night',
  [ShiftRateTypesEnum._24H]: '24 Hr',
  [ShiftRateTypesEnum.CALL]: 'On Call',
};

/**
 * Shift type icons (Font Awesome class names)
 */
export const SHIFT_TYPE_ICONS: Record<ShiftRateTypesEnum, string> = {
  [ShiftRateTypesEnum.DAY]: 'fa-sun',
  [ShiftRateTypesEnum.NIGHT]: 'fa-moon',
  [ShiftRateTypesEnum._24H]: 'fa-clock',
  [ShiftRateTypesEnum.CALL]: 'fa-phone',
};

/**
 * Shift type icon colors (Tailwind text color classes).
 * Matches the color scheme used in ShiftConfiguration buttons.
 */
export const SHIFT_TYPE_ICON_COLORS: Record<ShiftRateTypesEnum, string> = {
  [ShiftRateTypesEnum.DAY]: 'tw:text-amber-500',
  [ShiftRateTypesEnum.NIGHT]: 'tw:text-blue-500',
  [ShiftRateTypesEnum._24H]: 'tw:text-green-600',
  [ShiftRateTypesEnum.CALL]: 'tw:text-zinc-500',
};

export interface ShiftTypeStyles {
  color: string;
  borderColor: string;
  backgroundColor: string;
}

export const SHIFT_TYPE_STYLES: Record<ShiftTypesEnum, ShiftTypeStyles> = {
  [ShiftTypesEnum.DAY]: {
    color: 'tw:text-amber-700',
    borderColor: 'tw:border-amber-100',
    backgroundColor: 'tw:bg-amber-50',
  },
  [ShiftTypesEnum.NIGHT]: {
    color: 'tw:text-blue-700',
    borderColor: 'tw:border-blue-100',
    backgroundColor: 'tw:bg-blue-50',
  },
  [ShiftTypesEnum._24H]: {
    color: 'tw:text-green-700',
    borderColor: 'tw:border-green-100',
    backgroundColor: 'tw:bg-green-50',
  },
};
